import React from 'react';
import {
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import { addTaskProgress, editTaskProgress } from './taskprogress.apis';
import { ITaskProgress } from './taskprogress.models';

type Props = {
  task?: ITaskProgress;
  onChanged?: () => void;
};

type State = {
  saving: boolean;
  reportedrate: number;
  reportsummary: string;
  constraintsnotes: string;
  nextsteps: string;
  remarks: string;
};

export default class TaskProgressEditor extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = this.stateFromTask(props.task);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.task?.rid !== this.props.task?.rid || prevProps.task?.tki !== this.props.task?.tki) {
      this.setState(this.stateFromTask(this.props.task));
    }
  }

  stateFromTask = (task?: ITaskProgress): State => ({
    saving: false,
    reportedrate: task?.rpr || 0,
    reportsummary: task?.sum || '',
    constraintsnotes: task?.con || '',
    nextsteps: task?.nxt || '',
    remarks: task?.rmk || '',
  });

  save = async () => {
    const { task } = this.props;
    if (!task) return;

    const { reportedrate, reportsummary, constraintsnotes, nextsteps, remarks } = this.state;

    this.setState({ saving: true });

    const payload = {
      recid: task.rid,
      activityplanid: task.api,
      taskid: task.tki,
      reportmodeid: task.rmi || 1,
      sourceid: task.soi || 1,
      stateid: task.ssi || 1,
      progressdate: task.pgd || new Date().toISOString().slice(0, 10),
      reportedrate,
      approvedrate: task.apr || 0,
      reporterid: task.rpi,
      approverid: task.api2,
      reporttitle: task.ttl || '',
      reportsummary,
      constraintsnotes,
      nextsteps,
      remarks,
      extrainfo: task.exi || {},
      status: task.sts ?? 1,
      stamp: task.stp,
    };

    if (task.rid && task.stp) {
      await editTaskProgress(payload);
    } else {
      await addTaskProgress(payload);
    }

    this.setState({ saving: false });
    this.props.onChanged?.();
  };

  render() {
    const { task } = this.props;
    const { saving, reportedrate, reportsummary, constraintsnotes, nextsteps, remarks } = this.state;

    if (!task) {
      return <Typography color="text.secondary">Select a task to update progress.</Typography>;
    }

    return (
      <Paper elevation={0} sx={{ p: 0 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2">
            Today&apos;s Progress Update
          </Typography>

          <TextField
            label="Reported Progress (%)"
            type="number"
            size="small"
            value={reportedrate}
            onChange={(e) => this.setState({ reportedrate: Number(e.target.value) })}
          />

          <TextField
            label="Progress Summary"
            multiline
            minRows={3}
            value={reportsummary}
            onChange={(e) => this.setState({ reportsummary: e.target.value })}
          />

          <TextField
            label="Constraints / Issues"
            multiline
            minRows={2}
            value={constraintsnotes}
            onChange={(e) => this.setState({ constraintsnotes: e.target.value })}
          />

          <TextField
            label="Next Steps"
            multiline
            minRows={2}
            value={nextsteps}
            onChange={(e) => this.setState({ nextsteps: e.target.value })}
          />

          <TextField
            label="Remarks"
            multiline
            minRows={2}
            value={remarks}
            onChange={(e) => this.setState({ remarks: e.target.value })}
          />

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
            onClick={this.save}
          >
            {task.rid ? 'Save Progress' : 'Start Progress'}
          </Button>
        </Stack>
      </Paper>
    );
  }
}